/**
 * Wizard step order and next/prev resolution.
 * Passengers and extras are separate UI stages that share the same
 * passenger and baggage state. selfComposePick is skipped unless the
 * search gate still needs it.
 */

export const BOOKING_FLOW_STEP_ORDER = [
  "search",
  "results",
  "selfComposePick",
  "flightDetails",
  "passengers",
  "extras",
  "review",
  "holdConfirmation",
  "draftPayment",
  "success",
];

export function resolveStepWhenPassengersSkipped(requestedStep) {
  return requestedStep;
}

export function resolveNextStepIndex(currentStep, gate, s) {
  const order = BOOKING_FLOW_STEP_ORDER;
  const i = order.indexOf(currentStep);
  if (i < 0 || i >= order.length - 1) return -1;
  let j = i + 1;
  while (j < order.length) {
    if (
      order[j] === "selfComposePick" &&
      (!gate.needsSelfComposePickStep || s.selectedFlightOffer != null)
    ) {
      j += 1;
      continue;
    }
    break;
  }
  return j < order.length ? j : -1;
}

export function canEnterStep(step, s, gate) {
  switch (step) {
    case "search":
      return true;
    case "results":
      return s.flowType !== null;
    case "selfComposePick":
      if (gate.partialSelfCompose) return false;
      if (!(gate.needsSelfComposePickStep || gate.allowSelfComposeChoice)) {
        return false;
      }
      return s.flowType !== null;
    case "flightDetails":
      return s.selectedFlightOffer !== null;
    case "extras":
      return s.selectedFlightOffer !== null;
    case "passengers":
      return s.selectedFlightOffer !== null;
    case "review":
      return s.passengersStepValid && s.passengers.length > 0;
    case "holdConfirmation":
      return (
        s.passengersStepValid &&
        s.passengers.length > 0 &&
        s.selectedFlightOffer !== null &&
        s.holdCreationStatus === "success" &&
        s.holdResult != null
      );
    case "draftPayment":
      return (
        s.passengersStepValid &&
        s.passengers.length > 0 &&
        s.selectedFlightOffer !== null
      );
    case "success":
      return s.bookingStatus === "confirmed";
    default:
      return false;
  }
}

export function canAdvanceFromStep(step, s, gate) {
  switch (step) {
    case "search":
      return s.flowType !== null;
    case "results": {
      if (gate.partialSelfCompose && !gate.outboundOnlyNoReturn) {
        return s.selectedFlightOffer != null;
      }
      if (gate.needsSelfComposePickStep) {
        return true;
      }
      return s.selectedFlightOffer !== null;
    }
    case "selfComposePick":
      return !!(
        s.selectedFlightOffer?.isSelfComposeCombined ||
        s.selectedFlightOffer?.source === "selfCompose"
      );
    case "flightDetails":
      return s.selectedFlightOffer !== null;
    case "extras":
      return s.passengersStepValid && s.passengers.length > 0;
    case "passengers":
      return s.passengersStepValid && s.passengers.length > 0;
    case "review":
      return (
        s.passengersStepValid &&
        s.passengers.length > 0 &&
        s.selectedFlightOffer !== null
      );
    case "holdConfirmation":
    case "draftPayment":
    case "success":
      return false;
    default:
      return false;
  }
}

/**
 * Same gate nextStep() uses. flightDetails + carry-on-only must resolve to extras,
 * not review (review cannot be entered without passenger names).
 */
export function resolveAppliedNextStep(currentStep, gate, s) {
  if (!canAdvanceFromStep(currentStep, s, gate || {})) return null;
  const nextIdx = resolveNextStepIndex(currentStep, gate || {}, s);
  if (nextIdx < 0) return null;
  const next = BOOKING_FLOW_STEP_ORDER[nextIdx];
  if (!canEnterStep(next, s, gate || {})) return null;
  return next;
}

/** Footer המשך on self-compose summary: both legs, not a pre-combined offer. */
export function isSelfComposeContinueEnabled(outboundDeal, inboundDeal) {
  return !!(outboundDeal && inboundDeal);
}

export function resolvePrevStepIndex(currentStep, s, gate) {
  const order = BOOKING_FLOW_STEP_ORDER;
  const i = order.indexOf(currentStep);
  if (i <= 0) return -1;
  let j = i - 1;
  while (j >= 0) {
    if (order[j] === "selfComposePick" && !gate.needsSelfComposePickStep) {
      const offer = s.selectedFlightOffer;
      const fromSelfCompose =
        offer &&
        (offer.isSelfComposeCombined || offer.source === "selfCompose");
      if (!fromSelfCompose) {
        j -= 1;
        continue;
      }
    }
    break;
  }
  return j;
}
